// "Duplicar com novas datas": escolhe o período da cópia e abre o cadastro preenchido.
import { useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { __t } from '../../../i18n/t';
import { dayLabel } from './discountStatus';
import { DupPreset, DupRange, dupRange } from './duplicateDates';

interface Props {
    /** O desconto a copiar; null = diálogo fechado. `hasCustomers`: tem lista de clientes (Painel 360 ou relatório). */
    record: { identify?: string | null; hasCustomers?: boolean | null } | null;
    /** Nome do cartão (descrição). */
    name: string;
    busy: boolean;
    onClose: () => void;
    onConfirm: (range: DupRange) => void;
    onExited: () => void;
}

const presetLabel = (preset: DupPreset): string => {
    switch (preset) {
        case DupPreset.weekend: return __t('Próximo fim de semana');
        case DupPreset.week: return __t('Próximos 7 dias');
        default: return __t('Próximos 30 dias');
    }
};

const PRESETS = [DupPreset.weekend, DupPreset.week, DupPreset.month];

const DuplicateDatesDialog = ({ record, name, busy, onClose, onConfirm, onExited }: Props) => {
    const last = useRef(record);
    if (record) last.current = record;
    const shown = record ?? last.current;

    const [preset, setPreset] = useState<DupPreset>(DupPreset.weekend);
    // Cada vez que abre, volta para "Próximo fim de semana".
    const [wasOpen, setWasOpen] = useState(false);
    if (Boolean(record) !== wasOpen) {
        setWasOpen(Boolean(record));
        if (record) setPreset(DupPreset.weekend);
    }

    if (!shown) return null;
    const range = dupRange(preset);
    // Meio-dia evita qualquer virada de dia ao mostrar a data de Brasília.
    const period = __t('Cópia de “%{nome}”, de %{inicio} a %{fim}', {
        nome: name,
        inicio: dayLabel(`${range.from}T12:00:00-03:00`),
        fim: dayLabel(`${range.to}T12:00:00-03:00`),
    });

    return (
        <Dialog
            open={Boolean(record)}
            onClose={busy ? undefined : onClose}
            aria-labelledby="duplicate-dates-title"
            aria-describedby="duplicate-dates-text"
            // O foco volta por onExited: o botão que abriu pode ter saído da lista quando o diálogo fecha.
            disableRestoreFocus
            slotProps={{ transition: { onExited } }}
            sx={{ '& .MuiDialog-paper': { width: 'calc(100% - 32px)', maxWidth: 520, m: 2, borderRadius: '20px', p: { xs: 0.5, sm: 1 } } }}
        >
            <DialogTitle id="duplicate-dates-title" sx={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>
                {__t('Duplicar %{codigo} com novas datas', { codigo: shown.identify || '' })}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                <DialogContentText id="duplicate-dates-text" sx={{ fontSize: 14.5, lineHeight: 1.5, color: '#475569' }}>
                    {shown.hasCustomers
                        ? __t('Copia tipo, postos, valores, limite e horários. Só o período muda. A lista de clientes e a notificação não vêm junto: você escolhe para quem vale de novo, em “Para quem”. Você revisa tudo antes de salvar.')
                        : __t('Copia tipo, postos, valores, limite, público e horários. Só o período muda. A notificação não vem junto. Você revisa tudo antes de salvar.')}
                </DialogContentText>
                <Box role="group" aria-label={__t('Novo período')} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {PRESETS.map(id => {
                        const on = id === preset;
                        return (
                            <ButtonBase
                                key={id}
                                aria-pressed={on}
                                disabled={busy}
                                onClick={() => setPreset(id)}
                                sx={{
                                    minHeight: 40, px: 2, borderRadius: '999px', border: `1px solid ${on ? '#4433B4' : '#7c8595'}`,
                                    bgcolor: on ? '#4433B4' : '#fff', color: on ? '#fff' : '#1e293b', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
                                    '&.Mui-focusVisible': { outline: '2px solid #4433B4', outlineOffset: 2 },
                                }}
                            >
                                {presetLabel(id)}
                            </ButtonBase>
                        );
                    })}
                </Box>
                <Box
                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', fontSize: 14.5, color: '#1e293b', p: '10px 12px', borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}
                >
                    <EventIcon aria-hidden="true" sx={{ fontSize: 18, color: '#4433B4', flex: 'none', mt: '2px' }} />
                    <span aria-live="polite" style={{ overflowWrap: 'anywhere' }}>{period}</span>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
                <Button
                    autoFocus
                    onClick={onClose}
                    disabled={busy}
                    variant="outlined"
                    sx={{ minHeight: 44, px: 2, textTransform: 'none', fontSize: 15, fontWeight: 500, color: '#1e293b', borderColor: '#7c8595', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#7c8595' } }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={() => onConfirm(range)}
                    disabled={busy}
                    variant="contained"
                    disableElevation
                    endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
                    sx={{ minHeight: 44, px: 2.25, textTransform: 'none', fontSize: 15, fontWeight: 700, bgcolor: '#79CF62', color: '#1e293b', '&:hover': { bgcolor: '#6cc456' }, '&.Mui-disabled': { bgcolor: 'rgba(121,207,98,.5)', color: 'rgba(30,41,59,.7)' } }}
                >
                    Revisar a cópia
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateDatesDialog;
